import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Users, TrendingUp, Award, Sparkles, Copy, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function DistributorRegister() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "", password: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/referrals/register", form);
      setDone(data);
      toast.success("Welcome to the Referral Partner Program!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (!done?.referral_link) return;
    navigator.clipboard.writeText(done.referral_link);
    toast.success("Link copied");
  };
  const wa = () => {
    if (!done?.referral_link) return;
    const text = `Try HisaabPro free for 30 days — Pakistan ka #1 Qist Manager. Sign up with my link: ${done.referral_link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 space-y-4" data-testid="dist-register-success">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          <div>
            <h1 className="font-display text-2xl font-bold">You're in!</h1>
            <p className="text-sm text-slate-600 mt-1">
              Welcome to the HisaabPro Referral Partner Program. Apna link share karein aur 30 % recurring commission kamayein.
            </p>
          </div>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3" data-testid="dist-register-code-card">
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Referral Code</div>
            <div className="font-mono font-bold text-lg text-amber-900 mt-0.5" data-testid="dist-register-code">{done.referral_code}</div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mt-2">Referral Link</div>
            <div className="font-mono text-xs text-amber-900 mt-0.5 break-all" data-testid="dist-register-link">{done.referral_link}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={copy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="dist-register-copy">
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
            <Button onClick={wa} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dist-register-wa">
              <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
            </Button>
          </div>
          <Button onClick={() => nav("/login")} variant="outline" className="w-full" data-testid="dist-register-success-cta">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.15]" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="font-display text-2xl font-bold">HisaabPro</div>
            <span className="text-[10px] text-emerald-400 uppercase font-bold tracking-wider">Referral Partner</span>
          </div>
        </div>
        <div className="relative z-10 space-y-5">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
            Refer &amp; Earn. <br /> 30 % recurring.
          </h1>
          <p className="text-slate-300 max-w-md">
            Apna unique referral link share karein. Har paid subscription par 30 % commission har month — jab tak woh subscriber active hai.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-md">
            <div className="rounded-md border border-slate-700 bg-slate-800/50 px-3 py-3">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              <div className="text-sm font-medium mt-2">30 %</div>
              <div className="text-[10px] text-slate-400 uppercase">Flat rate</div>
            </div>
            <div className="rounded-md border border-slate-700 bg-slate-800/50 px-3 py-3">
              <Award className="w-5 h-5 text-amber-400" />
              <div className="text-sm font-medium mt-2">Recurring</div>
              <div className="text-[10px] text-slate-400 uppercase">Every payment</div>
            </div>
            <div className="rounded-md border border-slate-700 bg-slate-800/50 px-3 py-3">
              <Sparkles className="w-5 h-5 text-sky-400" />
              <div className="text-sm font-medium mt-2">PKR 0</div>
              <div className="text-[10px] text-slate-400 uppercase">Joining fee</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">
          Free to join · No onboarding fee · Withdraw above PKR 1,000.
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="dist-register-form">
          <div>
            <h2 className="font-display text-3xl font-bold">Join the Partner Program</h2>
            <p className="text-slate-500 text-sm mt-1">Free to join — get your referral code instantly.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dist-reg-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="mobile">Mobile</Label>
              <Input id="mobile" required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="dist-reg-mobile" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="city">City</Label>
              <Input id="city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="dist-reg-city" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="dist-reg-email" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="dist-reg-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dist-reg-submit">
            {loading ? "Joining…" : "Join — It's Free"}
          </Button>
          <div className="text-sm text-slate-600 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-emerald-700 font-semibold">Log in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
